'use strict';

import EcsContact from '@/classes/contacts/EcsContact';
import EcsCrmContact from '@/classes/contacts/EcsCrmContact';
import { INTERACTION_STATE, PARTY_TYPE, COMPLETION_STATUS } from '@/constants/interaction/constants';
import {
  MEDIA_TYPE_TELEPHONY,
  MEDIA_TYPE_CHAT,
  MEDIA_TYPE_EMAIL,
  MEDIA_TYPE_SMS,
  INTERNAL_CALL,
  EXTERNAL_CALL,
  CALLBACK_TELEPHONY,
  CAMPAIGN_TELEPHONY,
  INTERNAL_CHAT,
  INCOMING_CHAT,
  OUTGOING_SMS,
  INCOMING_TELEPHONY,
  INCOMING_EMAIL,
  INCOMING_SMS,
  OUTGOING_EMAIL,
  DIRECT_INCOMING_TELEPHONY
} from '@/constants/socket-events';
import { HANDLED } from '@/constants/appConstants';
import store from '@/store';
import ConnectedParty from '@/classes/ConnectedParty';
import { splitCamelCasedWords } from '@/utils';

const loggedInAgentKey = 'AGENT/loggedInAgent';
const tmpContacts = {};

export default class EcsInteraction {
  constructor(interactionData = {}) {
    if (interactionData && interactionData.interactionId) {
      Object.assign(this, this._formatInteraction(interactionData));
    }
  }

  // --- PUBLIC GETTERS ---

  get customer() {
    if (!this.crm.contactId) {
      return null;
    }

    const storedContact = store.getters['CONTACTS/getContactV2'](this.crm.contactId);
    let result = storedContact || tmpContacts[this.crm.contactId];

    if (!result) {
      result = new EcsCrmContact({
        id: this.crm.contactId,
        firstName: this.customerName,
        email: this.customerEmail,
        phone1: this.isTelephony || this.isSms ? this.routing.origin : null,
        dnc: true
      });

      tmpContacts[this.crm.contactId] = result;
    }

    if (storedContact) {
      delete tmpContacts[this.crm.contactId];
    }

    return result;
  }

  get customerName() {
    const trimmedFullName = this.crm.customerFullName ? this.crm.customerFullName.replace(/\(.*\)/, '').trim() : '';
    return this.isSms ? this.routing.remoteParty : trimmedFullName;
  }

  get formattedCustomerFullName() {
    let customerFullNameToDisplay =
      (this.crm && this.crm.customerFullName) || (this.routing && this.routing.remoteParty);

    // Show the formatted agent info if its Internal
    if (this.isInternal && this.connectedPartiesListNew && this.connectedPartiesListNew.length > 0) {
      customerFullNameToDisplay = `${this.connectedPartiesListNew[0].name} (${this.routing.origin})`;
    } else {
      const contact = store.getters['CONTACTS/getContactV2'](this.crm.contactId);
      const customerFullName = contact ? contact.name : this.customerName;

      if (customerFullName.trim()) {
        if (this.customer && customerFullName !== this.customer.email) {
          customerFullNameToDisplay = customerFullName;
        }
      }
    }

    return (customerFullNameToDisplay || '').replace(/\(.*\)/, '').trim();
  }

  get customerEmail() {
    let result = '';

    if (this.isChat && this.crm.customerFullName) {
      result = this.crm.customerFullName.replace(/.+\(/g, '').replace(/\)/g, '');
    } else if (this.isEmail) {
      result = this.routing.origin;
    }

    return result;
  }

  get originatorContact() {
    let result = null;

    const currentAgent = store.getters[loggedInAgentKey];

    if (this.isConsultReceiver) {
      result = this.consultRequestor;
    } else if (currentAgent.endPoint === this.routing.origin) {
      result = currentAgent;
    } else if (this.customer) {
      result = this.customer.name
        ? this.customer
        : new EcsContact({
            id: this.crm.contactId,
            name: this.routing.origin,
            endPoint: this.isTelephony || this.isSms ? this.routing.origin : null
          });
    } else {
      result = store.getters['CONTACTS/getContactByEndPoint'](this.routing.origin);
    }

    return (
      result ||
      new EcsContact({
        id: null,
        name: this.routing.origin,
        endPoint: this.isTelephony || this.isSms ? this.routing.origin : null
      })
    );
  }

  get destinationContact() {
    let result = null;

    const currentAgent = store.getters[loggedInAgentKey];

    if (this.isOutgoingEmail) {
      const email = store.getters['INTERACTIONS/emails/getEmailByInteractionId'](this.interactionId);
      if (email && email.contactId) {
        result = store.getters['CONTACTS/getContact'](email.contactId);
      }
    } else if (currentAgent.endPoint === this.routing.destination) {
      result = currentAgent;
    } else if (this.customer) {
      result = this.customer;
    } else {
      result = store.getters['CONTACTS/getContactByEndPoint'](this.routing.destination);
    }

    return (
      result ||
      new EcsContact({
        id: null,
        name: this.routing.destination,
        endPoint: this.isTelephony ? this.routing.destination : null
      })
    );
  }

  get receptor() {
    if (this.isCallback) {
      return this.destinationContact;
    } else if (this.isOutgoingEmail) {
      return {
        id: this.crm.contactId
      };
    } else {
      return this.routing.origin === store.getters[loggedInAgentKey].endPoint
        ? this.destinationContact
        : this.originatorContact;
    }
  }

  get connectedParties() {
    let initiator = new ConnectedParty({
      id: this.destinationContact.id,
      mane: this.destinationContact.name,
      partyType: EcsInteraction.partyType.agent
    });

    if (this.isCustomerHandling && this.customer && this.customer.id) {
      initiator = new ConnectedParty({
        id: this.customer.id,
        mane: this.customerName,
        partyType: EcsInteraction.partyType.customer
      });
    }

    if (this.isEmail) {
      initiator = new ConnectedParty({
        id: !this.routing.remoteParty ? this.crm.contactId : undefined,
        name: this.routing.remoteParty || 'New Email'
      });
    }

    if (this.isSms) {
      const participant = this.connectedPartiesListNew[0];
      initiator = new ConnectedParty({
        id: participant.id || this.customer ? this.customer.id : null,
        name: this.formattedCustomerFullName
      });
    }

    if (this.isOffered) {
      initiator = new ConnectedParty({
        id: this.customer ? this.customer.id : null,
        name: this.formattedCustomerFullName
      });
    }

    if (this.isInternal) {
      initiator = new ConnectedParty({
        id: this.originatorContact.id,
        name: this.originatorContact.name,
        partyType: EcsInteraction.partyType.agent
      });
    }

    let consulted = null;

    if (this.consult.receiver) {
      consulted = new ConnectedParty({
        id: this.consult.receiver.id,
        name: this.consult.receiver.name,
        partyType: EcsInteraction.partyType.agent
      });
    }

    const receptor = this.getHandledBy(consulted);

    const originatorLeft = this.connectedPartiesListNew.findIndex(c => c.id === initiator.id) === -1;

    return {
      originatorLeft,
      originatedBy: initiator,
      handledBy: receptor,
      consultingWith: this.isConference ? null : consulted,
      conferenceWith: this.isConference ? consulted : null
    };
  }

  getHandledBy(consulted) {
    let receptor = new ConnectedParty({
      id: this.routing.agentId,
      mane: this.routing.agentName,
      partyType: EcsInteraction.partyType.agent
    });

    if (this.isInternal) {
      receptor = new ConnectedParty({
        id: this.destinationContact.id,
        name: this.destinationContact.name,
        partyType: EcsInteraction.partyType.agent
      });
    }

    if (this.isPendingConsult && this.connectedPartiesListNew[0].id !== consulted.id) {
      receptor = this.connectedPartiesListNew.length
        ? new ConnectedParty({
            id: this.connectedPartiesListNew[0].id,
            name: this.connectedPartiesListNew[0].name,
            partyType: EcsInteraction.partyType.agent
          })
        : receptor;
    }

    return receptor;
  }

  get completedByDelegatingToSupervisor() {
    return this.completion.completionStatusId === COMPLETION_STATUS.delegateToSupervisor;
  }

  get isDirectCall() {
    return this.isTelephony && this.interactionType === DIRECT_INCOMING_TELEPHONY;
  }

  get isTelephony() {
    return this.interactionMediaType === MEDIA_TYPE_TELEPHONY;
  }

  get isChat() {
    return this.interactionMediaType === MEDIA_TYPE_CHAT;
  }

  get isEmail() {
    return this.interactionMediaType === MEDIA_TYPE_EMAIL;
  }

  get isSms() {
    return this.interactionMediaType === MEDIA_TYPE_SMS;
  }

  get isOutgoingEmail() {
    return this.interactionType === OUTGOING_EMAIL;
  }

  get isInternal() {
    return this.interactionType === INTERNAL_CALL || this.interactionType === INTERNAL_CHAT;
  }

  get isInternalChat() {
    return this.interactionType === INTERNAL_CHAT;
  }

  get isIncomingChat() {
    return this.interactionType === INCOMING_CHAT;
  }

  get isInternalCall() {
    return this.interactionType === INTERNAL_CALL;
  }

  get isIncomingTelephony() {
    return this.interactionType === INCOMING_TELEPHONY;
  }

  get isIncomingEmail() {
    return this.interactionType === INCOMING_EMAIL;
  }

  get isExternalCall() {
    return this.interactionType.toUpperCase() === EXTERNAL_CALL;
  }

  get isCustomerHandling() {
    return [INCOMING_CHAT, INCOMING_TELEPHONY, INCOMING_EMAIL, INCOMING_SMS].includes(
      this.interactionType.toUpperCase()
    );
  }

  get isPendingConsult() {
    return (
      this.isConsultReceiver &&
      [INTERACTION_STATE.handling, INTERACTION_STATE.offered].includes(this.interactionStateId)
    );
  }

  get isConference() {
    return this.interactionStateId === INTERACTION_STATE.inConference;
  }

  get isPreview() {
    return this.interactionStateId === INTERACTION_STATE.preview;
  }

  get isOnWrapUp() {
    return this.interactionStateId === INTERACTION_STATE.wrapUp;
  }

  get isDialing() {
    return this.interactionStateId === INTERACTION_STATE.dialing;
  }

  get isHandling() {
    return this.interactionStateId === INTERACTION_STATE.handling;
  }

  get isHandled() {
    return this.interactionStateId === INTERACTION_STATE.handled;
  }

  get isDelegatedToSupervisor() {
    return this.interactionStateId === INTERACTION_STATE.delegatedToSupervisor;
  }

  get isHistoric() {
    return [INTERACTION_STATE.handled, INTERACTION_STATE.delegatedToSupervisor, INTERACTION_STATE.canceled].includes(
      this.interactionStateId
    );
  }

  get isCanceled() {
    return this.interactionStateId === INTERACTION_STATE.canceled;
  }

  get isConsulting() {
    return this.interactionStateId === INTERACTION_STATE.consulting;
  }

  get isOffered() {
    return this.interactionStateId === INTERACTION_STATE.offered;
  }

  get isCallback() {
    return this.interactionType.toUpperCase() === CALLBACK_TELEPHONY;
  }

  get isCampaign() {
    return this.interactionType.toUpperCase() === CAMPAIGN_TELEPHONY;
  }

  get isTransferable() {
    return !this.isConsulting && !this.isConference && !this.isConsultReceiver;
  }

  get consultRequestorDisplayName() {
    let displayName = '';
    if (this.isPendingConsult) {
      displayName = this.consult.requestor.name || '';
    }
    return displayName;
  }

  get consultRequestor() {
    return this.consult ? this.consult.requestor : null;
  }

  get consultReceiver() {
    return this.consult ? this.consult.receiver : null;
  }

  get isConsultRequestor() {
    return this.consult && !!this.consult.receiver;
  }

  get isConsultReceiver() {
    return this.consult && !!this.consult.requestor;
  }

  get parsedDemands() {
    return (this.routing.demands || []).map(demand => splitCamelCasedWords(demand));
  }

  get mediaTypeIcon() {
    if (this.isTelephony) {
      return 'mdi-phone';
    } else if (this.isChat) {
      return 'mdi-message-text';
    } else if (this.isEmail || this.isOutgoingEmail) {
      return 'mdi-email';
    } else if (this.isSms) {
      return 'mdi-chat-processing-outline';
    }
    return undefined;
  }

  // static getters

  static get mediaTypes() {
    return {
      telephony: MEDIA_TYPE_TELEPHONY,
      chat: MEDIA_TYPE_CHAT,
      email: MEDIA_TYPE_EMAIL,
      sms: MEDIA_TYPE_SMS
    };
  }

  static get interactionTypes() {
    return {
      campaignTelephony: CAMPAIGN_TELEPHONY,
      incomingTelephony: INCOMING_TELEPHONY,
      callbackTelephony: CALLBACK_TELEPHONY,
      internalCall: INTERNAL_CALL,
      externalCall: EXTERNAL_CALL,
      incomingChat: INCOMING_CHAT,
      internalChat: INTERNAL_CHAT,
      incomingEmail: INCOMING_EMAIL,
      outgoingEmail: OUTGOING_EMAIL,
      incomingSMS: INCOMING_SMS,
      outgoingSMS: OUTGOING_SMS
    };
  }

  static get partyType() {
    return PARTY_TYPE;
  }

  static get interactionState() {
    return INTERACTION_STATE;
  }

  // --- PRIVATE FUNCTIONS ---

  /**
   * Create an EcsInteraction object from ECS event data
   * @param event
   * @returns {{
   *    interactionMediaType: *, interactionId: *,
   *    timing: {entryTime: *, wrapUpTime: *, automaticAcception: *, numberOfPreviousAttempts: *, waitingTime: *, answeredWithinGoalTime: *, noAnswerTimeout: *},
   *    interactionStateName: *, interactionStateId: *,
   *    routing: {agentId: *, businessProcessId: *, routingInfo: *, campaignId: *, origin: *, businessProcessName: *, destination: *, agentName: *, campaignName: *, demands: *},
   *    completionStatuses: *,
   *    disposition: {
   *      dispositionCodeName: *, dispositionCodeMandatory: *, dispositionCodeId: *, dispositionCodeCategoryName: *,
   *      dispositionCodeSubCategoryId: *, dispositionCodeCategoryId: *, dispositionCodeSubCategoryName: *},
   *    interactionType: *, connectedPartiesList: *, links: *,
   *    consultation: {consulteeTypeId: *, consulteeUri: *, consultRequestorId: *, consulteeTypeName: *, consultRequestorName: *},
   *    crm: {customerFullName: *, contactId: *, previousAttempts: *, remarks: *}}}
   * @private
   */
  _formatInteraction(interaction) {
    const {
      // ECS Event/API payload
      timestamp,
      wrapUpTime,
      answeredWithinGoalTime,
      automaticAcception,
      interactionId,
      interactionMediaType,
      interactionType,
      contactId,
      automaticPopupLinks,

      consultRequestorId,
      consultRequestorName,
      consulteeId,
      consulteeName,
      consulteeUri,
      consulteeTypeId,

      elements,

      origin,
      campaignId,
      campaignName,
      remarks,
      demands,
      previousAttempts,
      numberOfPreviousAttempts,
      entryTime,
      links,
      completionStatuses,
      completionStatusId,
      completionStatusName,
      customerFullName,
      routingInfo,
      waitingTime,
      noAnswerTimeout,
      dispositionCodeMandatory,
      destination,
      dispositionCodeCategoryId,
      dispositionCodeCategoryName,
      dispositionCodeSubCategoryId,
      dispositionCodeSubCategoryName,
      dispositionCodeId,
      dispositionCodeName,
      interactionStateId,
      interactionStateName,
      interactionStateTime,
      lastStateStartTime,
      wrapUpTimeout,
      connectedPartiesList,
      connectedPartiesListNew,
      businessProcessId,
      businessProcessName,
      agentId,
      agentName,
      consult,
      totalWaitingDuration,
      remoteParty,
      // EcsInteraction Objects
      timing,
      routing,
      disposition,
      // The following fields are persisted around the application due some data is returned during Interaction Lifecycle
      crm,
      completionStatus,
      completion,

      transfer,
      interactionHandlerName,
      interactionHandlerId,
      interactionHandlerTypeName,
      interactionHandlerTypeId,
      transferredToDestination,
      callbackRecordingFile,
      callbackRecordingTranscription,
      previewTime,
      systemDID,
      totalWaitingDurationinBP,

      startTime,
      endTime,
      talkTime,
      waitTime,
      handlingDuration,
      directCallAnsweredAt,

      interactionVariables
    } = interaction;
    return {
      interactionId,
      interactionMediaType: interactionMediaType.toUpperCase(),
      interactionType: interactionType.toUpperCase(),
      interactionStateId,
      interactionStateName,
      interactionStateTime,
      interactionVariables,
      completionStatuses,
      links,
      connectedPartiesList,
      connectedPartiesListNew,
      timestamp,
      callbackRecordingFile,
      callbackRecordingTranscription,
      previewTime,
      systemDID,
      totalWaitingDurationinBP,
      automaticPopupLinks: automaticPopupLinks || [],
      timing: timing || {
        answeredWithinGoalTime,
        automaticAcception,
        entryTime,
        noAnswerTimeout,
        numberOfPreviousAttempts,
        waitingTime: totalWaitingDuration || waitingTime,
        wrapUpTime,
        wrapUpTimeout,
        lastStateStartTime,

        // Only for /Contacts/action/GetAgentContactInteractionsHistory
        startTime,
        endTime,
        talkTime,
        waitTime,
        handlingDuration,
        directCallAnsweredAt
      },
      routing: routing || {
        agentId,
        agentName,
        businessProcessId,
        businessProcessName,
        campaignId,
        campaignName,
        destination,
        demands,
        origin,
        routingInfo,
        remoteParty
      },
      disposition: disposition || {
        dispositionCodeMandatory,
        dispositionCodeCategoryId,
        dispositionCodeCategoryName,
        dispositionCodeSubCategoryId,
        dispositionCodeSubCategoryName,
        dispositionCodeId,
        dispositionCodeName
      },
      crm: crm || {
        contactId,
        customerFullName,
        previousAttempts,
        remarks: remarks || []
      },
      // The following fields are persisted around the application due some data is returned during Interaction Lifecycle
      completionStatus: completionStatus || {
        completionStatusId: completionStatusId || HANDLED,
        completionStatusName: completionStatusName || 'Handled',
        completionStatusInformation: null
      },
      completion: completion || {
        completionStatusId,
        completionStatusName: completionStatus
      },
      consult: consult || {
        requestor:
          consultRequestorId && store.getters[loggedInAgentKey].id !== consultRequestorId
            ? {
                id: consultRequestorId,
                name: consultRequestorName,
                endPoint: elements && elements.length >= 3 ? elements[2].origin : null
              }
            : null,
        receiver:
          consulteeId && contactId !== consulteeId
            ? {
                id: consulteeId,
                name: consulteeName,
                endPoint: consulteeUri,
                type: consulteeTypeId
              }
            : null
      },
      transfer: transfer || {
        handlerName: interactionHandlerName,
        handlerId: interactionHandlerId,
        handlerType: interactionHandlerTypeName,
        handlerTypeId: interactionHandlerTypeId,
        endPoint: transferredToDestination
      }
    };
  }
}
